import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import API_ENDPOINTS from 'utils/api/endpoints';
import { toast } from 'react-toastify';
import { toastStyle } from 'utils/helper';

const getApiErrorMessage = (error, fallback) => {
  const data = error.response?.data;

  if (!data) {
    return fallback;
  }

  if (typeof data === 'string') {
    return data;
  }

  if (typeof data.error === 'string') {
    return data.error;
  }

  if (typeof data === 'object') {
    const firstValue = Object.values(data)[0];
    if (Array.isArray(firstValue)) {
      return firstValue.join(' ');
    }
    if (typeof firstValue === 'string') {
      return firstValue;
    }
  }

  return fallback;
};

// Budget Actions
export const fetchBudgets = createAsyncThunk(
  'api/fetchBudgets',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.get_budgets();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchBudgetById = createAsyncThunk(
  'api/fetchBudgetById',
  async (budgetId, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.get_budget_by_id(budgetId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createBudget = createAsyncThunk(
  'api/createBudget',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.create_budget(payload);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create budget';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateBudget = createAsyncThunk(
  'api/updateBudget',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.update_budget(payload);
      toast.success('Budget updated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update budget';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteBudget = createAsyncThunk(
  'api/deleteBudget',
  async (budgetId, { rejectWithValue }) => {
    try {
      await API_ENDPOINTS.budgets.delete_budget(budgetId);
      toast.success('Budget deleted successfully', toastStyle);
      return budgetId;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete budget';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const duplicateBudget = createAsyncThunk(
  'api/duplicateBudget',
  async (payload, { rejectWithValue }) => {
    try {
      const endpoint = payload?.withValues
        ? API_ENDPOINTS.budgets.duplicate_budget_values(payload)
        : API_ENDPOINTS.budgets.duplicate_budget(payload);
      const response = await endpoint;
      toast.success('Budget duplicated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message =
        error.response?.data?.error || 'Failed to duplicate budget';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Folder Actions
export const fetchFolders = createAsyncThunk(
  'api/fetchFolders',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.get_folders();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createFolder = createAsyncThunk(
  'api/createFolder',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.create_folder(payload);
      toast.success('Folder created successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create folder';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFolder = createAsyncThunk(
  'api/updateFolder',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.budgets.update_folder(payload);
      toast.success('Folder updated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update folder';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFolder = createAsyncThunk(
  'api/deleteFolder',
  async (folderId, { rejectWithValue }) => {
    try {
      await API_ENDPOINTS.budgets.delete_folder(folderId);
      toast.success('Folder deleted successfully', toastStyle);
      return folderId;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete folder';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Swimlane Actions
export const fetchSwimlanes = createAsyncThunk(
  'api/fetchSwimlanes',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      if (payload.type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.get_swimlanes(payload.id);
      } else if (payload.type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.get_line_items(payload.id);
      } else if (payload.type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.get_line_item_children(payload.id);
      }
      const response = await endpoint;
      // Match React Query structure: return the full response object
      return { data: response, type: payload.type, id: payload.id };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createSwimlane = createAsyncThunk(
  'api/createSwimlane',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      let apiPayload;

      if (payload.type === 'swimlane') {
        apiPayload = {
          budget: payload.budgetId,
          name: payload.name,
          section: payload.section || null,
          position: payload.position,
        };
        endpoint = API_ENDPOINTS.budgets.create_swimlane;
      } else if (payload.type === 'line-item') {
        apiPayload = {
          budget: payload.budgetId,
          parent: payload.swimlane || payload.selectedItem?.id,
          name: payload.name,
          position: payload.position,
        };
        endpoint = API_ENDPOINTS.budgets.create_line_item;
      } else if (payload.type === 'line-item-children') {
        apiPayload = {
          budget: payload.budgetId,
          parent: payload.selectedItem?.id || payload.swimlane,
          name: payload.name,
          position: payload.position,
        };
        endpoint = API_ENDPOINTS.budgets.create_line_item_children;
      }

      if (!endpoint || !apiPayload) {
        throw new Error('Unknown category type');
      }

      const response = await endpoint(apiPayload);
      const responseData = response.data || {};
      const rootSwimlaneId =
        responseData.swimlane ??
        (payload.type === 'swimlane'
          ? responseData.id
          : payload.swimlane ??
            payload.selectedItem?.swimlane ??
            payload.selectedItem?.id ??
            responseData.parent);

      return {
        ...responseData,
        budget: responseData.budget ?? payload.budgetId,
        parent: responseData.parent ?? apiPayload.parent ?? null,
        swimlane: rootSwimlaneId,
        swimlane_detail_sheets: responseData.swimlane_detail_sheets ?? [],
        swimlane_item_detail_sheets:
          responseData.swimlane_item_detail_sheets ?? [],
        budget_swimlane_lineitem: responseData.budget_swimlane_lineitem ?? [],
        bs_lineitem_children:
          responseData.bs_lineitem_children ?? responseData.children ?? [],
        children:
          responseData.children ?? responseData.bs_lineitem_children ?? [],
      };
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to create category');
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSwimlane = createAsyncThunk(
  'api/updateSwimlane',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      let apiPayload;

      if (payload.type === 'swimlane') {
        apiPayload = {
          id: payload.id,
          name: payload.name,
          cost: payload.cost,
          ...(Object.prototype.hasOwnProperty.call(payload, 'account_number')
            ? { account_number: payload.account_number }
            : {}),
          ...(Object.prototype.hasOwnProperty.call(payload, 'section')
            ? { section: payload.section || null }
            : {}),
        };
        endpoint = API_ENDPOINTS.budgets.update_swimlane;
      } else if (payload.type === 'line-item') {
        apiPayload = {
          id: payload.id,
          name: payload.name,
          cost: payload.cost,
          ...(Object.prototype.hasOwnProperty.call(payload, 'account_number')
            ? { account_number: payload.account_number }
            : {}),
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item;
      } else if (payload.type === 'line-item-children') {
        apiPayload = {
          id: payload.id,
          name: payload.name,
          cost: payload.cost,
          ...(Object.prototype.hasOwnProperty.call(payload, 'account_number')
            ? { account_number: payload.account_number }
            : {}),
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item_children;
      }

      const response = await endpoint(apiPayload);
      const isAccountNumberOnlyUpdate =
        Object.prototype.hasOwnProperty.call(payload, 'account_number') &&
        Object.keys(payload).every(key =>
          ['type', 'id', 'account_number'].includes(key)
        );
      if (!payload.suppressSuccessToast && !isAccountNumberOnlyUpdate) {
        toast.success('Item updated successfully', toastStyle);
      }
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update item';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSwimlane = createAsyncThunk(
  'api/deleteSwimlane',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;

      if (payload.type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.delete_swimlane;
      } else if (payload.type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item;
      } else if (payload.type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item_children;
      }

      await endpoint(payload.id);
      return payload.id;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete item';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Fringe Actions
export const fetchFringes = createAsyncThunk(
  'api/fetchFringes',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.fringes.list_fringe(filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createFringe = createAsyncThunk(
  'api/createFringe',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.fringes.create_fringe(payload);
      toast.success('Fringe created successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create fringe';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateFringe = createAsyncThunk(
  'api/updateFringe',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.fringes.update_fringe(payload);
      toast.success('Fringe updated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update fringe';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteFringe = createAsyncThunk(
  'api/deleteFringe',
  async (payload, { rejectWithValue }) => {
    try {
      await API_ENDPOINTS.fringes.delete_fringe(payload);
      toast.success('Fringe deleted successfully', toastStyle);
      return payload;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete fringe';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// User Profile Actions
export const fetchUserProfile = createAsyncThunk(
  'api/fetchUserProfile',
  async (_, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.account_settings.get_user_profile();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'api/updateUserProfile',
  async (payload, { rejectWithValue }) => {
    try {
      const endpoint = payload?.id
        ? API_ENDPOINTS.account_settings.update_user_profile(payload)
        : API_ENDPOINTS.account_settings.create_user_profile(payload);
      const response = await endpoint;
      toast.success('Profile updated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update profile';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Swimlane Details Actions
export const fetchSwimlaneDetails = createAsyncThunk(
  'api/fetchSwimlaneDetails',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      if (payload.type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.get_swimlane_detailed_sheet(
          payload.id
        );
      } else if (payload.type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.get_line_item_detailed_sheet(
          payload.id
        );
      } else if (payload.type === 'line-item-children') {
        endpoint = API_ENDPOINTS.budgets.get_line_item_children_detailed_sheet(
          payload.id
        );
      }
      const response = await endpoint;
      return { data: response.data, type: payload.type, id: payload.id };
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createSwimlaneDetails = createAsyncThunk(
  'api/createSwimlaneDetails',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      let apiPayload;

      if (payload.type === 'swimlane') {
        apiPayload = {
          ...payload,
          swimlane: payload.swimlane,
        };
        endpoint = API_ENDPOINTS.budgets.create_swimlane_detail_sheet;
      } else if (payload.type === 'line-item') {
        apiPayload = {
          ...payload,
          line_item: payload.line_item,
        };
        endpoint = API_ENDPOINTS.budgets.create_line_item_detailed_sheet;
      } else if (payload.type === 'line-item-children') {
        apiPayload = {
          ...payload,
          line_item: payload.line_item,
        };
        endpoint =
          API_ENDPOINTS.budgets.create_line_item_children_detailed_sheet;
      }

      const response = await endpoint(apiPayload);
      toast.success('Detail created successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to create detail';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateSwimlaneDetails = createAsyncThunk(
  'api/updateSwimlaneDetails',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;
      let apiPayload;

      if (payload.type === 'swimlane') {
        apiPayload = {
          ...payload,
          swimlane: payload.swimlane,
        };
        endpoint = API_ENDPOINTS.budgets.update_swimlane_detail_sheet;
      } else if (payload.type === 'line-item') {
        apiPayload = {
          ...payload,
          line_item: payload.line_item,
        };
        endpoint = API_ENDPOINTS.budgets.update_line_item_detailed_sheet;
      } else if (payload.type === 'line-item-children') {
        apiPayload = {
          ...payload,
          line_item: payload.line_item,
        };
        endpoint =
          API_ENDPOINTS.budgets.update_line_item_children_detailed_sheet;
      }

      const response = await endpoint(apiPayload);
      toast.success('Detail updated successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to update detail';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteSwimlaneDetails = createAsyncThunk(
  'api/deleteSwimlaneDetails',
  async (payload, { rejectWithValue }) => {
    try {
      let endpoint;

      if (payload.type === 'swimlane') {
        endpoint = API_ENDPOINTS.budgets.delete_swimlane_details_sheet;
      } else if (payload.type === 'line-item') {
        endpoint = API_ENDPOINTS.budgets.delete_line_item_details_sheet;
      } else if (payload.type === 'line-item-children') {
        endpoint =
          API_ENDPOINTS.budgets.delete_line_item_children_details_sheet;
      }

      await endpoint(payload.id);
      toast.success('Detail deleted successfully', toastStyle);
      return payload.id;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to delete detail';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Linked Fringes Actions
export const linkFringes = createAsyncThunk(
  'api/linkFringes',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await API_ENDPOINTS.linked_fringes.link_fringes(payload);
      toast.success('Fringes linked successfully', toastStyle);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to link fringes';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const unlinkFringes = createAsyncThunk(
  'api/unlinkFringes',
  async (payload, { rejectWithValue }) => {
    try {
      await API_ENDPOINTS.linked_fringes.unlink(payload);
      toast.success('Fringes unlinked successfully', toastStyle);
      return payload;
    } catch (error) {
      const message = error.response?.data?.error || 'Failed to unlink fringes';
      toast.error(message, toastStyle);
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Initial state
const initialState = {
  budgets: [],
  folders: [],
  swimlanes: {},
  swimlaneDetails: {},
  fringes: [],
  userProfile: null,
  loading: {
    budgets: false,
    folders: false,
    swimlanes: false,
    swimlaneDetails: false,
    fringes: false,
    userProfile: false,
  },
  error: {
    budgets: null,
    folders: null,
    swimlanes: null,
    swimlaneDetails: null,
    fringes: null,
    userProfile: null,
  },
};

// API Slice
const apiSlice = createSlice({
  name: 'api',
  initialState,
  reducers: {
    clearError: (state, action) => {
      const { type } = action.payload;
      if (state.error[type]) {
        state.error[type] = null;
      }
    },
    clearAllErrors: state => {
      Object.keys(state.error).forEach(key => {
        state.error[key] = null;
      });
    },
    updateSwimlaneData: (state, action) => {
      const { type, id, data } = action.payload;
      if (!state.swimlanes[type]) {
        state.swimlanes[type] = {};
      }
      state.swimlanes[type][id] = data;
    },
  },
  extraReducers: builder => {
    // Budget reducers
    builder
      .addCase(fetchBudgets.pending, state => {
        state.loading.budgets = true;
        state.error.budgets = null;
      })
      .addCase(fetchBudgets.fulfilled, (state, action) => {
        state.loading.budgets = false;
        state.budgets = action.payload;
      })
      .addCase(fetchBudgets.rejected, (state, action) => {
        state.loading.budgets = false;
        state.error.budgets = action.payload;
      })
      .addCase(createBudget.fulfilled, (state, action) => {
        state.budgets.push(action.payload);
      })
      .addCase(updateBudget.fulfilled, (state, action) => {
        const index = state.budgets.findIndex(
          budget => budget.id === action.payload.id
        );
        if (index !== -1) {
          state.budgets[index] = action.payload;
        }
      })
      .addCase(deleteBudget.fulfilled, (state, action) => {
        state.budgets = state.budgets.filter(
          budget => budget.id !== action.payload
        );
      })
      .addCase(duplicateBudget.fulfilled, (state, action) => {
        state.budgets.push(action.payload);
      });

    // Folder reducers
    builder
      .addCase(fetchFolders.pending, state => {
        state.loading.folders = true;
        state.error.folders = null;
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.loading.folders = false;
        state.folders = action.payload;
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.loading.folders = false;
        state.error.folders = action.payload;
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload);
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const index = state.folders.findIndex(
          folder => folder.id === action.payload.id
        );
        if (index !== -1) {
          state.folders[index] = action.payload;
        }
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter(
          folder => folder.id !== action.payload
        );
      });

    // Swimlane reducers
    builder
      .addCase(fetchSwimlanes.pending, state => {
        state.loading.swimlanes = true;
        state.error.swimlanes = null;
      })
      .addCase(fetchSwimlanes.fulfilled, (state, action) => {
        state.loading.swimlanes = false;
        const { type, id, data } = action.payload;
        if (!state.swimlanes[type]) {
          state.swimlanes[type] = {};
        }
        // Store the response object to match React Query structure
        state.swimlanes[type][id] = data;
      })
      .addCase(fetchSwimlanes.rejected, (state, action) => {
        state.loading.swimlanes = false;
        state.error.swimlanes = action.payload;
      })
      .addCase(createSwimlane.fulfilled, (state, action) => {
        // This will be handled by refetching the swimlanes
      })
      .addCase(updateSwimlane.fulfilled, (state, action) => {
        // This will be handled by refetching the swimlanes
      })
      .addCase(deleteSwimlane.fulfilled, (state, action) => {
        // This will be handled by refetching the swimlanes
      });

    // Fringe reducers
    builder
      .addCase(fetchFringes.pending, state => {
        state.loading.fringes = true;
        state.error.fringes = null;
      })
      .addCase(fetchFringes.fulfilled, (state, action) => {
        state.loading.fringes = false;
        state.fringes = action.payload;
      })
      .addCase(fetchFringes.rejected, (state, action) => {
        state.loading.fringes = false;
        state.error.fringes = action.payload;
      })
      .addCase(createFringe.fulfilled, (state, action) => {
        state.fringes.push(action.payload);
      })
      .addCase(updateFringe.fulfilled, (state, action) => {
        const index = state.fringes.findIndex(
          fringe => fringe.id === action.payload.id
        );
        if (index !== -1) {
          state.fringes[index] = action.payload;
        }
      })
      .addCase(deleteFringe.fulfilled, (state, action) => {
        state.fringes = state.fringes.filter(
          fringe => fringe.id !== action.payload.id
        );
      });

    // User Profile reducers
    builder
      .addCase(fetchUserProfile.pending, state => {
        state.loading.userProfile = true;
        state.error.userProfile = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.loading.userProfile = false;
        state.userProfile = action.payload;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.loading.userProfile = false;
        state.error.userProfile = action.payload;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.userProfile = action.payload;
      });

    // Swimlane Details reducers
    builder
      .addCase(fetchSwimlaneDetails.pending, state => {
        state.loading.swimlaneDetails = true;
        state.error.swimlaneDetails = null;
      })
      .addCase(fetchSwimlaneDetails.fulfilled, (state, action) => {
        state.loading.swimlaneDetails = false;
        const { type, id, data } = action.payload;
        if (!state.swimlaneDetails[type]) {
          state.swimlaneDetails[type] = {};
        }
        state.swimlaneDetails[type][id] = data;
      })
      .addCase(fetchSwimlaneDetails.rejected, (state, action) => {
        state.loading.swimlaneDetails = false;
        state.error.swimlaneDetails = action.payload;
      })
      .addCase(createSwimlaneDetails.fulfilled, (state, action) => {
        // This will be handled by refetching the details
      })
      .addCase(updateSwimlaneDetails.fulfilled, (state, action) => {
        // This will be handled by refetching the details
      })
      .addCase(deleteSwimlaneDetails.fulfilled, (state, action) => {
        // This will be handled by refetching the details
      });
  },
});

export const { clearError, clearAllErrors, updateSwimlaneData } =
  apiSlice.actions;

// Selectors
export const selectBudgets = state => state.api.budgets;
export const selectFolders = state => state.api.folders;
export const selectSwimlanes = state => state.api.swimlanes;
export const selectFringes = state => state.api.fringes;
export const selectUserProfile = state => state.api.userProfile;
export const selectLoading = state => state.api.loading;
export const selectErrors = state => state.api.error;

export const selectSwimlanesByType = (state, type) =>
  state.api.swimlanes[type] || {};
export const selectSwimlanesByTypeAndId = (state, type, id) =>
  state.api.swimlanes[type]?.[id]?.data || [];

export const selectSwimlaneDetails = state => state.api.swimlaneDetails;
export const selectSwimlaneDetailsByType = (state, type) =>
  state.api.swimlaneDetails[type] || {};
export const selectSwimlaneDetailsByTypeAndId = (state, type, id) =>
  state.api.swimlaneDetails[type]?.[id] || [];

export default apiSlice.reducer;
